export interface SurnameCategory {
  id: number;
  name: string;
  isValidated?: boolean;
}

export interface GotraSurname {
  id: number;
  name: string;
  order?: number;
  isValidated?: boolean;
  categories: SurnameCategory[];
}

export interface UpdateSurnamePayload {
  name?: string;
  order?: number;
}

export interface Pravara {
  id: number;
  name: string;
  order?: number;
  groupId?: number | null;
  isValidated?: boolean;
}

export interface PravaraInGroup {
  id: number;
  name: string;
  order?: number;
  isValidated?: boolean;
}

export interface PravaraGroup {
  id: number;
  order?: number;
  isValidated?: boolean;
  pravaras: PravaraInGroup[];
}

export interface UpdatePravaraPayload {
  name?: string;
  order?: number;
  groupId?: number | null;
}

export interface CreatePravaraGroupPayload {
  order?: number;
  pravaraIds?: number[];
}

export interface UpdatePravaraGroupPayload {
  order?: number;
  pravaraIds?: number[];
}

export interface AddPravaraPayload {
  name: string;
  order?: number;
}

export interface OrderableItem {
  id: number;
  order?: number;
}

export const sortByOrder = <T extends OrderableItem>(items: T[]): T[] =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const orderDiff = (a.item.order ?? 0) - (b.item.order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return a.index - b.index;
    })
    .map(({ item }) => item);

export const sortSurnamesByOrder = (surnames: GotraSurname[]): GotraSurname[] =>
  sortByOrder(surnames);

export const sortPravarasByOrder = (pravaras: Pravara[]): Pravara[] => sortByOrder(pravaras);

export const sortPravaraGroupsByOrder = (groups: PravaraGroup[]): PravaraGroup[] =>
  sortByOrder(groups);

export const getUngroupedPravaras = (pravaras: Pravara[]): Pravara[] =>
  sortPravarasByOrder(pravaras.filter((p) => p.groupId == null));

export const getOrderUpdatesAfterMove = (
  items: OrderableItem[],
  itemId: number,
  direction: -1 | 1,
): Array<{ id: number; order: number }> | null => {
  const sorted = sortByOrder(items);
  const index = sorted.findIndex((item) => item.id === itemId);
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || targetIndex >= sorted.length) return null;

  const orderCounts = new Map<number, number>();
  for (const item of items) {
    const current = item.order ?? 0;
    orderCounts.set(current, (orderCounts.get(current) || 0) + 1);
  }

  const reordered = [...sorted];
  const [moved] = reordered.splice(index, 1);
  reordered.splice(targetIndex, 0, moved);

  return reordered
    .map((item, order) => ({
      id: item.id,
      order,
      currentOrder: item.order ?? 0,
    }))
    .filter(
      ({ currentOrder, order }) =>
        currentOrder !== order || (orderCounts.get(currentOrder) || 0) > 1,
    )
    .map(({ id, order }) => ({ id, order }));
};

export interface Gotra {
  id: number;
  name: string;
  isValidated?: boolean;
  pravara: Pravara[];
  pravaraGroups?: PravaraGroup[];
  surnames: GotraSurname[];
}

export const normalizeGotra = (gotra: Gotra): Gotra => ({
  ...gotra,
  isValidated: Boolean(gotra.isValidated),
  pravara: (gotra.pravara || []).map((pravara) => ({
    ...pravara,
    isValidated: Boolean(pravara.isValidated),
  })),
  pravaraGroups: (gotra.pravaraGroups || []).map((group) => ({
    ...group,
    isValidated: Boolean(group.isValidated),
    pravaras: (group.pravaras || []).map((pravara) => ({
      ...pravara,
      isValidated: Boolean(pravara.isValidated),
    })),
  })),
  surnames: (gotra.surnames || []).map((surname) => ({
    ...surname,
    isValidated: Boolean(surname.isValidated),
    categories: (surname.categories || []).map((category) => ({
      ...category,
      isValidated: Boolean(category.isValidated),
    })),
  })),
});

export const filterGotraByValidation = (
  gotra: Gotra,
  options: { showValidated: boolean; showUnvalidated: boolean },
): Gotra | null => {
  const { showValidated, showUnvalidated } = options;
  if (!showValidated && !showUnvalidated) return null;

  const matchesValidation = (isValidated?: boolean) => {
    const validated = Boolean(isValidated);
    return validated ? showValidated : showUnvalidated;
  };

  const filteredPravaraGroups = (gotra.pravaraGroups || [])
    .filter((group) => matchesValidation(group.isValidated))
    .map((group) => ({
      ...group,
      pravaras: (group.pravaras || []).filter((pravara) =>
        matchesValidation(pravara.isValidated),
      ),
    }));

  const filteredGroupIds = new Set(filteredPravaraGroups.map((group) => group.id));
  const filteredPravara = (gotra.pravara || []).filter((pravara) => {
    if (!matchesValidation(pravara.isValidated)) return false;
    if (pravara.groupId != null) return filteredGroupIds.has(pravara.groupId);
    return true;
  });

  const filteredSurnames = (gotra.surnames || [])
    .filter((surname) => matchesValidation(surname.isValidated))
    .map((surname) => ({
      ...surname,
      categories: (surname.categories || []).filter((category) =>
        matchesValidation(category.isValidated),
      ),
    }));

  const gotraMatches = matchesValidation(gotra.isValidated);
  const hasMatchingPravara =
    filteredPravaraGroups.length > 0 || filteredPravara.length > 0;
  if (!gotraMatches && filteredSurnames.length === 0 && !hasMatchingPravara) return null;

  return {
    ...gotra,
    pravara: filteredPravara,
    pravaraGroups: filteredPravaraGroups,
    surnames: filteredSurnames,
  };
};

export const parseNameList = (text: string) =>
  Array.from(new Set(text.split(/[\n,]+/).map((part) => part.trim()).filter(Boolean)));

export const formatValidationOptionLabel = (name: string, isValidated?: boolean) =>
  `${name} (${isValidated ? 'verified' : 'unverified'})`;

export interface CreateGotraPayload {
  name: string;
  pravara: string[];
  surnames?: Array<{
    name: string;
    categories?: Array<{ name: string }>;
  }>;
}

export interface UpdateGotraPayload {
  name?: string;
  pravara?: string[];
  surnames?: Array<{
    name: string;
    categories?: Array<{ name: string }>;
  }>;
}

export interface GotraSeedResult {
  message: string;
  sourceRecords: number;
  uniqueGotras: number;
  mergedDuplicateRows: number;
  created: number;
  updated: number;
  jsonPath: string;
}

export interface GotraSuggestion {
  id: number;
  name: string;
  phone: string;
  text: string;
  createdAt: string;
}

export interface CreateGotraSuggestionPayload {
  name: string;
  phone: string;
  text: string;
}
